/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import { Type } from 'class-transformer';

////////////////////////////////////////////////////
// RESULT VALIDITY
////////////////////////////////////////////////////

export type ValidityState = {
  severity: ValiditySeverity;
  reason?: ValidityReason;
};
export type ValidityStates = ValidityState[];
type ValiditySeverity = 'off' | 'near' | 'on' | 'frost';
type ValidityReason = 'condensationExceeded' | 'condensationNear' | 'vaporPressure' | 'temperatureHigh' | 'pressureHigh' | 'frostForming' | 'tdfCalcInUse' | 'rhiCalcInUse';

export function getValidityState(reason?: ValidityReason): ValidityState {
  if (!reason) {
    return { severity: 'off' };
  }
  switch (reason) {
    case 'condensationNear':
    case 'temperatureHigh':
    case 'pressureHigh':
    case 'frostForming':
      return { severity: 'near', reason: reason };
    case 'rhiCalcInUse':
      return { severity: 'frost', reason: reason};
    case 'tdfCalcInUse':
      return { severity: 'frost', reason: reason};
    case 'condensationExceeded':
    case 'vaporPressure':
      return { severity: 'on', reason: reason };
  }
}

export function addValidityIfNotExist(validityReason: ValidityReason, validityStates: ValidityStates) {
  const validityExists = validityStates.some((v) => v.reason === validityReason)
  let validity: ValidityState;
  if (!validityExists) {
    validity = getValidityState(validityReason)
  } else {
    validity = {severity: 'off'}
  }
  return validity
}

////////////////////////////////////////////////////
// PARAMETERS
////////////////////////////////////////////////////

// public types
export type ParameterModifier = string | number;
export type ParameterName = keyof ResultParameters;
export type ParameterValueElement = keyof ParameterValues;

export class ParameterValues {
  constructor(public tolerance: number = 0) {
    if (new.target === ParameterValues) {
      throw new TypeError('Abstract class: Cannot construct ParameterValues instances directly');
    }
  }
  public get value(): ParameterModifier {
    return 0;
  }
  public set value(val: ParameterModifier) {}
}

export class NumericParameterValues extends ParameterValues {
  constructor(private _value: number = 0) {
    super();
  }
  public get value() {
    return this._value;
  }
  public set value(val: number) {
    this._value = val;
  }
}

export class StringParameterValues extends ParameterValues {
  constructor(private _value: string = '') {
    super();
  }
  public get value() {
    return this._value;
  }
  public set value(val: string) {
    this._value = val;
  }
}

class ResultParameters {
  // ambient conditions

  @Type(() => NumericParameterValues) temperature = new NumericParameterValues();
  @Type(() => NumericParameterValues) pressure = new NumericParameterValues();
  @Type(() => NumericParameterValues) elevation = new NumericParameterValues();
  @Type(() => StringParameterValues) gasType = new StringParameterValues();
  @Type(() => StringParameterValues) psychrometer = new StringParameterValues();

  // the actual parameters
  @Type(() => NumericParameterValues) relativeHumidity = new NumericParameterValues();
  @Type(() => NumericParameterValues) wetBulbTemperature = new NumericParameterValues();
  @Type(() => NumericParameterValues) enthalpy = new NumericParameterValues();
  @Type(() => NumericParameterValues) waterConcentration = new NumericParameterValues();
  @Type(() => NumericParameterValues) dewPointTemperature = new NumericParameterValues();
  @Type(() => NumericParameterValues) absoluteHumidity = new NumericParameterValues();
  @Type(() => NumericParameterValues) waterVaporPressure = new NumericParameterValues();
  @Type(() => NumericParameterValues) waterConcentrationWetBasis = new NumericParameterValues();
  @Type(() => NumericParameterValues) dewFrostPointTemperature = new NumericParameterValues();
  @Type(() => NumericParameterValues) mixingRatio = new NumericParameterValues();
  @Type(() => NumericParameterValues) waterVaporSaturationPressure = new NumericParameterValues();
  @Type(() => NumericParameterValues) relativeHumidityDewFrost = new NumericParameterValues();
  @Type(() => NumericParameterValues) gasDensity = new NumericParameterValues();
  @Type(() => NumericParameterValues) specificVolume = new NumericParameterValues();
}

export class CalculatorResult {
  public validityState: ValidityState = getValidityState();
  public validityStates: ValidityState[] = [];
  @Type(() => ResultParameters) public parameters: ResultParameters = new ResultParameters();
}
